import { useState, useEffect, useRef , useMemo} from "react";
import { get, cloneDeep, map, find} from "lodash";
import moment from "moment";
import { DatePicker , Select, Tooltip, Checkbox, Spin, Empty} from "antd";
import SimpleSelect from "@components/simpleSelect/simpleSelect";
import { dateFormatConfig } from "@utils/constants";
import { getClientName } from "@utils/helpers";
import './StructureForm.scss';
import Icon from "@assets/icon_config";
import { useDropdowns, usePatientData } from "@utils/customHooks";
import { Spinner } from "@components/healthemSpinner/healthemSpinner";
// import MultiSelect from "@components/multiSelect/multiSelect";
import { useLocation } from "react-router";
import useSession from "@utils/useSession";

const { RangePicker } = DatePicker;

const { Option } = Select;

export function SingleDropdown({value , handleChange ,  label, defaultValue, placeholder, disabled , dropdown_key , isMandatory, options}: any) {

  const { loadingOptions, getOptions , filterDropdowns} = useDropdowns();

  return (
    <div className="new-outreach-from_dropdown"  onClick={() => {
      if(!disabled && dropdown_key){
        getOptions(dropdown_key);
      }
    }}>
      <label className="new-outreach-from_dropdown__label">{label}{isMandatory ? <span className="required">*</span> : null}</label>
      <SimpleSelect
        handleSelect={(value: string, option: any) => {
          handleChange(value);
        }}
        value={value}
        defaultValue={defaultValue}
        options={get(filterDropdowns, `${dropdown_key}`, options || [])}
        loadingOptions={loadingOptions}
        placeholder={placeholder}
        disabled={disabled}
        allowClear={true}
        //below empty object for style is placed so the default style of simpleselect will not be applied
        style={{}}
      />
    </div>
  );
}

export function DateTimePicker({value , handleChange ,  label, disabled , isMandatory, showTime, min, max}: any) {

  function dateChange(value: any) {
    handleChange(value);
  }

  return (
    <div className="new-outreach-from_datetime">
      <label className="new-outreach-from_datetime__label">{label}{isMandatory ? <span className="required">*</span> : null}</label>
      <DatePicker
        showTime={showTime}
        disabled={disabled}
        format={dateFormatConfig.get(getClientName())[showTime ? "dateTimeColumnFilterDisplayFormatWithTime" : "defaultDateFormat"]}
        value={value}
        onChange={(value: any) => {
          dateChange(value);
        }}
        disabledDate={
          (date) => {
            if (min && date.isBefore(min)) {
              return true
            }
            if (max && date.isAfter(max)) {
              return true
            }
            return false
          }
        }
      />
    </div>
  );
}

export function ImageRadio({value , handleChange ,  label, data , isMandatory}: any) {

  const activeColor = "#EF6D2A";


  function handleSelection(name : string) {
    handleChange(name);
  }

  return (
    <div className="new-outreach-from_imageradio">
      <label className="new-outreach-from_imageradio__label">{label}{isMandatory ? <span className="required">*</span> : null}</label>
      <div className="new-outreach-from_imageradio_content">
        {data.map((rad: any) => {
          const {icon , name} = rad;

          return (
            <div className={`new-outreach-from_imageradio_content__radio ${name === value ? 'active' : ''}`} onClick={() => handleSelection(name)}>
              <div>
                <Icon name={icon} colorOfBrand={name === value ? activeColor : ''}/>
              </div>
              <div>{name}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function MultiDropdown({value , handleChange ,  label, disabled, dropdown_key , isMandatory , removeOption}: any) {

  const { loadingOptions, getOptions, filterDropdowns } = useDropdowns();

  const session = useSession();

  function handleSelect({ value }: any) {
    handleChange(value);
  }
  const {patientId} = usePatientData();

  let {options , additionalData} = useMemo(() => {

    let additionalData;

    if(dropdown_key === 'AllUserLst') {
      additionalData= {patientId};
    }

    const uniqueKey = dropdown_key + (additionalData ? JSON.stringify(additionalData) : '');
    let options = get(filterDropdowns , `${uniqueKey}` , []);

    if(removeOption.includes('current_user')) {
      options = options.filter((o : any) => o.value !== get(session , 'user.id' , ''));
    }

    return {options , additionalData};
  },[dropdown_key, filterDropdowns, patientId, removeOption, session])


  return (
    <div
      className="new-outreach-from_dropdown"
      onClick={() => {
        if (!disabled && dropdown_key) {
          getOptions(dropdown_key , '' , additionalData); 
        }
      }}
    >
      <label className="new-outreach-from_dropdown__label">{label}{isMandatory ? <span className="required">*</span> : null}</label>
      <MultiSelect handleSelect={(value) => handleSelect({ value })} value={value} options={options} loadingOptions={loadingOptions} disabled={disabled} />
    </div>
  );
}

export function DateTimeRangePicker({ value, handleChange, label, isMandatory }: any) {
  return (
    <>
      <label className="new-outreach-from_dropdown__label">
        {label}
        {isMandatory ? <span className="required">*</span> : null}
      </label>
      <div className="new-outreach-from_datepicker" onClick={(e) => e.stopPropagation()}>
        <div className="reminderDateLabel">
          <span className="icon-calendar f18" />
          <span>Date Range : </span>
        </div>
        <RangePicker
          value={value}
          picker="date"
          format={dateFormatConfig.get(getClientName())["defaultDateFormat"]}
          allowClear={true}
          dropdownClassName="filter-date-picker"
          ranges={{
            Today: [moment(), moment()],
            Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
            "Last 2 days": [moment().subtract(2, "days"), moment()],
            "Last 7 days": [moment().subtract(7, "days"), moment()],
          }}
          onChange={(range: any) => {
            if(range) {
              range[0] = range[0].startOf(('day'));
              range[1] = range[1].endOf(('day'))
            }
            handleChange(range);
          }}
        />
      </div>
    </>
  );
}

export function StructureForm({structureData , handleData , entryData} : any) {

  return (
    <div className="new-outreach-from">
      {structureData.map((formInput : any) => {

        const value = get(entryData , `${get(formInput ,'struct_field_id','')}.value`);

        const key = get(formInput ,'struct_field_id' , '');

        const type = get(formInput ,'struct_disp_type' , '');

        const dbColKey = get(formInput , 'additional_info.db_col_key' , '');

        return <StructureInput key={key} input={formInput} value={value} handleChange={(value : any) => handleData(key , value , type , dbColKey)}/>;
      })}
    </div>
  );
}



export function StructureInput({ input , value , handleChange}: any) {
  const { struct_disp_type, rule, label , dropdown_key , additional_info} = input;

  const { defaultValue, disabled = false, placeholder, isMandatory = false , noDisplay = {} , removeOption = []} = rule || {};

  const { iconData, options } = additional_info || {};

  const location = useLocation();

  if(get(noDisplay , 'location' , '').split('$|').includes(location.pathname.slice(1))) {
    return <></>
  }

  if (struct_disp_type === "DROPDOWN") {
    return <SingleDropdown {...{ value , handleChange, label, defaultValue, placeholder, disabled , dropdown_key , isMandatory, options }} />;
  }

  if (struct_disp_type === "DATETIME") {
    const { min, max } = rule || {}
    return <DateTimePicker {...{ value , handleChange, label , isMandatory, showTime: true, min, max}} />;
  }

  if (struct_disp_type === "DATE") {
    const { min, max, disabled } = rule || {}
    return <DateTimePicker {...{ value , handleChange, label , isMandatory, showTime: false, min, max, disabled}} />;
  }

  if (struct_disp_type === "DATETIMERANGE") {
    return <DateTimeRangePicker {...{ value , handleChange, label , isMandatory}} />;
  }

  if (struct_disp_type === "IMAGE_RADIO") {
    return <ImageRadio {...{ value , handleChange, label , data : iconData , isMandatory}} />;
  }

  if(struct_disp_type === "MULTIIDROPDOWN") {
    return <MultiDropdown {...{value , handleChange,  label , defaultValue, placeholder, disabled , isMandatory , dropdown_key , removeOption}} />;
  }

  return <div>Please enter the correct Input type</div>;
}



const OptionVal = ({ option, handleChange, selectedValues, disabled }: any) => {
  const optionRef = useRef(null);

  const [showTooltip, setShowTooltip] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    console.log("OptionVal.useEffect");
    if (get(optionRef, "current.clientWidth", 0) < get(optionRef, "current.scrollWidth", 0)) {
      setShowTooltip(true);
    }
  });

  return showTooltip ? (
    <Tooltip title={option.title}>
      <div ref={optionRef} className="custom-multiSelect__checkbox">
        <Checkbox disabled={disabled} value={option.value} onChange={(e) => handleChange({ source: "checkbox", e })} checked={selectedValues.includes(option.value)} />
        <span>{option.label || option.title}</span>
      </div>
    </Tooltip>
  ) : (
    <div ref={optionRef} className="custom-multiSelect__checkbox">
      <Checkbox disabled={disabled} value={option.value} onChange={(e) => handleChange({ source: "checkbox", e })} checked={selectedValues.includes(option.value)} />
      <span>{option.label || option.title}</span>
    </div>
  );
};

const MultiSelect = (props: { handleSelect: (arg0: any) => void; value: any; options: any; disabled?: boolean; loadingOptions?: boolean; placeholder?: string }) => {
  
  const [dropdownOpen , setDropdownOpen] = useState<boolean>(false);
  const [selectedValues, setselectedValues] = useState<string[]>([]);

  useEffect(() => {
    console.log("MultiSelect.useEffect");
    if (props.value?.split(":").length > 0) {
      setselectedValues(props.value?.split("$|"));
    } else if (!props.value) {
      setselectedValues([]);
    }
  }, [props.value]);

  const dropdownOptions = useMemo(() => {
    const selectedOptionValues =  props.value?.split("$|");
    let temp_dropdownOptions = cloneDeep(props.options);
    let selectedOptions: any[] = [];
    let unselectedOptions: any[] = [];
    if (temp_dropdownOptions.length > 0) {
      temp_dropdownOptions.forEach((dropdownOption: any) => {
        if (selectedOptionValues?.indexOf(dropdownOption.value) === -1) {
          unselectedOptions.push(dropdownOption);
        } else {
          selectedOptions.push(dropdownOption);
        }
      });
      return [...selectedOptions, ...unselectedOptions]
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[props.options , dropdownOpen])


  const handleChange = ({ source, e }: any) => {
    console.log("MultiSelect.handleChange")
    let updatedSelectedValues: string[] = [];
    if (source === "checkbox") {
      if (e.target.checked && !selectedValues.includes(e.target.value)) {
        updatedSelectedValues = [...selectedValues, e.target.value];
      } else {
        updatedSelectedValues = selectedValues.filter((val) => val !== e.target.value);
      }
    } else if (source === "dropdown") {
      if(!selectedValues.includes(e)){
        updatedSelectedValues = [...selectedValues, e];
      } else {
        updatedSelectedValues =  selectedValues?.filter((s : string) => s !== e);
      }
    }
    // the below filter is to remove the empty string from the updatedSelectedValues
    updatedSelectedValues = updatedSelectedValues.filter((val) => val.trim() !== '');
    setselectedValues(updatedSelectedValues);
    props.handleSelect(updatedSelectedValues.join("$|"));
  };

  const tooltipText = useMemo(() => {
    const value = props.value || '';

    const ids = value.split('$|');
    
    const selectedOptions = (props.options || []).filter((o : any) => ids.includes(o.value)).map((o : any) => o.label);
    
    return selectedOptions.join(' , ');
  },[props.options, props.value]);

  return (
    <Tooltip title={tooltipText}>
      <Select
        className={`${props.disabled ? "custom-multi-select-disable-input-text-area" : ""} ui-select`}
        open={dropdownOpen}
        placeholder={props.placeholder ? props.placeholder : "Select Option"}
        optionFilterProp="children"
        onChange={(option: any) => handleChange({ source: "dropdown", e: option })}
        onClick={() => setDropdownOpen(true)}
        onBlur={() => setDropdownOpen(false)}
        value={
          !dropdownOpen
            ? selectedValues.length > 0
              ? `${
                  selectedValues.length > 0
                    ? get(
                        find(dropdownOptions, (option) => option.value === get(selectedValues, "[0]", "")),
                        "label",
                        ""
                      )
                    : ""
                } 
      ${selectedValues.length > 1 ? "(+" + (selectedValues.length - 1) + ")" : ""}`
              : undefined
            : ""
        }
        showSearch={!props.disabled}
        notFoundContent={props.loadingOptions ? <Spinner size="small" /> : <Empty />}
        filterOption={(input, option: any) => {
          return option.value.toLowerCase().includes(input.toLowerCase()) || get(option, "label", "").toLowerCase().includes(input.toLowerCase());
        }}
        getPopupContainer={(trigger) => trigger.parentNode}
      >
        {(dropdownOptions || []).length > 0
          ? map(dropdownOptions, (option) => {
              return (
                <Option key={option.value} value={option.value} label={option.label} className="filters-row-option" disabled={props.disabled}>
                  <OptionVal option={option} handleChange={handleChange} selectedValues={selectedValues} disabled={props.disabled} />
                </Option>
              );
            })
          : !props.loadingOptions && (
              <Option key="1" value="" className="filters-row-option">
                {" "}
              </Option>
            )}
      </Select>
    </Tooltip>
  );
}